import { Dispatch } from 'redux';
import {
    setCalendarType,
    setCompactCalendarData,
    setScrollCalendarData,
} from '../actions/infiniteCalendarSeries';
import { TlzEnvironment } from '../api/constants';
import { getEventCalendarDates } from '../api/eventDates';
import { getMonthAndYearFromDate } from '../components/InfiniteCalendarSeries/utils';
import {
    mapEventDatesToCompactCalendarData,
    mapEventDatesToScrollCalendarData,
} from '../components/TimedEntrySeries/CalendarContainer/utils';
import { getAppLocale } from '../selectors/app';
import { getBffRequestBaseParams } from '../selectors/bff';
import {
    getParentEventInfo,
    getScrollCalendarData,
    getTimezone,
} from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { CheckoutState } from '../state';
import {
    CalendarType,
    DateApiResponse,
    type ScrollCalendarData,
} from '../state/infiniteCalendarSeries';

export type EventDateParams = {
    eventId: string;
    env: TlzEnvironment;
    month?: number;
    year?: number;
};

const shouldSkipLoading = (
    previousCalendarData: ScrollCalendarData,
    month?: number,
    year?: number,
): boolean => {
    if (month == null || year == null) {
        return false;
    }
    return Boolean(previousCalendarData?.years?.[year]?.months?.[month]);
};

export const _initializeScrollCalendarData = ({
    previousScrollCalendarData,
    month,
    year,
    dispatch,
}: {
    previousScrollCalendarData: ScrollCalendarData;
    month: number;
    year: number;
    dispatch: Dispatch<CheckoutState>;
}) => {
    const calendarData = _getLoadingCalendarData(
        previousScrollCalendarData,
        month,
        year,
    );
    dispatch(setScrollCalendarData(calendarData));
};

export const _getLoadingCalendarData = (
    previousCalendarData: ScrollCalendarData,
    month: number,
    year: number,
): ScrollCalendarData => {
    const calendarData = previousCalendarData || { years: {} };
    calendarData.years ??= {};
    calendarData.years[year] ??= { months: {} };
    calendarData.years[year].months[month] ??= {
        isLoading: true,
        dates: {},
    };
    return calendarData;
};

const updateScrollCalendar = ({
    response,
    state,
    month,
    year,
    dispatch,
}: {
    response: DateApiResponse;
    state: CheckoutState;
    month: number;
    year: number;
    dispatch: Dispatch<CheckoutState>;
}) => {
    const previousScrollCalendarData = getScrollCalendarData(state) || {};
    _initializeScrollCalendarData({
        previousScrollCalendarData,
        month,
        year,
        dispatch,
    });
    const timezone = getTimezone(state);
    const scrollCalendarData = mapEventDatesToScrollCalendarData({
        eventDates: response.eventDates,
        previousScrollCalendarData,
        month,
        year,
        timezone,
    });
    dispatch(setScrollCalendarData(scrollCalendarData));
};

const updateCompactCalendar = (
    response: DateApiResponse,
    state: CheckoutState,
    dispatch: Dispatch<CheckoutState>,
) => {
    const locale = getAppLocale(state).replace('_', '-');
    const timezone = getTimezone(state);
    const compactCalendarData = mapEventDatesToCompactCalendarData(
        response.eventDates,
        timezone,
        locale,
    );
    dispatch(setCompactCalendarData(compactCalendarData));
};

export const _fetchAndUpdateCalendarData = async ({
    dispatch,
    getState,
    month,
    year,
}: {
    dispatch: Dispatch<CheckoutState>;
    getState: () => CheckoutState;
    month?: number;
    year?: number;
}): Promise<void> => {
    try {
        const state = getState();
        const { id: eventId } = getParentEventInfo(state);
        const { env } = getBffRequestBaseParams(state);
        const params: EventDateParams = {
            eventId,
            env,
        };

        if (month != null && year != null) {
            // Note about `params.month = month + 1` 👇:
            // The month argument will have zero-indexed month, meaning January is 0.
            // The `/dates` API expects a one-indexed month, meaning January is 1.
            // For December 2024, the month and year arguments will be 11 and 2024, respectively, and these will be passed to the API as 12 and 2024.
            // For January 2025, the month and year arguments will be 0 and 2025, but they will be passed as 1 and 2025, respectively.
            params.month = month + 1;
            params.year = year;
        }

        const response = await getEventCalendarDates(params);
        if (response.calendarType === CalendarType.scrollCalendar) {
            const { month: _month, year: _year } =
                month == null && year == null
                    ? {
                          ...getMonthAndYearFromDate(
                              response.eventDates[0].date,
                          ),
                      }
                    : { month, year };

            // About `_month as number, _year as number` 👇:
            // When month and year are not provided, it is the first api call to `/dates` API which will return eventDates and calendarType.
            // In that case, month and year will be taken from the first element of the `response.eventDates` array
            // On subsequent calls to `/dates` function, month and year will be provided by `ScrollableCalendar` (via `onMonthLoad` prop)
            // So _month, _year will always have values.
            updateScrollCalendar({
                response,
                state,
                month: _month as number,
                year: _year as number,
                dispatch,
            });
        } else {
            updateCompactCalendar(response, state, dispatch);
        }
        dispatch(setCalendarType(response.calendarType));
    } catch (error) {
        console.error('Error fetching calendar data:', error);
    }
};

/**
 * Used in scroll calendar. `ScrollableCalendar` will pass month and year
 */
export const loadDatesForMonth =
    (month: number, year: number) =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();
        const previousScrollCalendarData = getScrollCalendarData(state) || {};
        if (shouldSkipLoading(previousScrollCalendarData, month, year)) {
            return;
        }
        _initializeScrollCalendarData({
            previousScrollCalendarData,
            month,
            year,
            dispatch,
        });
        await _fetchAndUpdateCalendarData({
            dispatch,
            getState,
            month,
            year,
        });
    };

export const loadInitialDates =
    () =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        await _fetchAndUpdateCalendarData({
            dispatch,
            getState,
        });
    };
